import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import { usePopUpsV2 } from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import { useAppDispatch } from "hooks";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { onDIPtrigger } from "redux/slices";
import { Step1, Step2, Step3 } from "./lib";
import { TutorialRoot, TutorialV2GlobalStyles } from "./styles";

const TOTAL_STEPS = 3;

export const TutorialV2 = () => {
	const { close } = usePopUpsV2();
	const dispatch = useAppDispatch();
	const { pathname } = useLocation();
	const [stepNumber, setStepNumber] = useState(1);
	const { postUserMetadata } = useWebStoreContext();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const portraitScale = useCalculateScale(375, 667, true);
	const landscapeScale = useCalculateScale(1133, 375, true);
	const sideLayoutLandscapeScale = useCalculateScale(667, 275, true);

	const closeTutorial = useCallback(() => {
		close({ variant: "tutorial-v2" });
	}, [close]);

	const reportTutorialAction = useCallback(
		(action: "complete" | "skip" | "step_render") => {
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "tutorial",
				event_type: "shop_visual_update",
				action,
				source: "tutorial",
				section_name: stepNumber.toString()
			});
		},
		[stepNumber]
	);

	const finishTutorial = useCallback(
		async (action: "complete" | "skip") => {
			reportTutorialAction(action);
			try {
				await postUserMetadata({ key: "hasSeenTutorial", value: "true" });
			} finally {
				closeTutorial();
				if (action === "complete") {
					dispatch(onDIPtrigger({ triggerType: "mainShopFtueComplete" }));
				}
			}
		},
		[closeTutorial, dispatch, postUserMetadata, reportTutorialAction]
	);

	const handleNext = useCallback(() => {
		if (stepNumber === TOTAL_STEPS) {
			void finishTutorial("complete");
			return;
		}
		setStepNumber((currentStep) => currentStep + 1);
	}, [finishTutorial, stepNumber]);

	const handleSkip = useCallback(() => {
		void finishTutorial("skip");
	}, [finishTutorial]);

	useEffect(() => {
		reportTutorialAction("step_render");
	}, [reportTutorialAction]);

	useEffect(() => {
		window.addEventListener("popstate", closeTutorial);
		return () => window.removeEventListener("popstate", closeTutorial);
	}, [closeTutorial]);

	useEffect(() => {
		if (pathname !== "/shop") {
			closeTutorial();
		}
	}, [closeTutorial, pathname]);

	const stepProps = {
		currentStep: stepNumber,
		totalSteps: TOTAL_STEPS,
		onNext: handleNext,
		onSkip: handleSkip
	};

	return (
		<TutorialRoot
			$landscapeScale={
				stepNumber === 3 ? sideLayoutLandscapeScale : landscapeScale
			}
			$portraitScale={portraitScale}
			data-tutorial-v2-root
		>
			<TutorialV2GlobalStyles />
			{stepNumber === 1 && <Step1 {...stepProps} />}
			{stepNumber === 2 && <Step2 {...stepProps} />}
			{stepNumber === 3 && <Step3 {...stepProps} />}
		</TutorialRoot>
	);
};
